import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { checkFormChanges } from "@/lib/checkFormChanges";
import {
  DropdownService,
  TenantUserRoleService,
  TenantUserService,
} from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantUser, Location } from "@/utils/types/tenantUser";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Tabs,
  Checkbox,
  Empty,
} from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useParams } from "react-router-dom";
import { CustomImageUpload } from "@/components/CustomImageUpload";

const UserFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useAppNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addressDetails, setAddressDetails] = useState<Location | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // Fetch dropdown roles
  const { data: roleOptions, isLoading: roleLoading } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  // Fetch user roles (only in edit mode)
  const {
    data: roleList,
    isLoading: roleListLoading,
    refetch: refetchRoleList,
  } = useQuery({
    queryKey: ["roleList", id],
    queryFn: () => TenantUserRoleService.getRoleListByUserId(id!),
    enabled: isEditMode,
  });

  // Fetch user data (only in edit mode)
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => TenantUserService.getTenantUserById(id!),
    enabled: isEditMode,
    retry: false,
  });

  // Sync roles when loaded
  useEffect(() => {
    if (roleList) {
      setSelectedRoles(roleList);
    }
  }, [roleList]);

  // Populate form when user data loads (Edit mode) or reset (Add mode)
  useEffect(() => {
    if (isEditMode && user?.user) {
      const locationDisplayName = user.user.location?.display_name ?? "";
      setAddressDetails(user.user.location || null);

      form.setFieldsValue({
        ...user.user,
        address: locationDisplayName, // safe fallback to empty string
      });
    } else {
      // Add mode: reset everything
      form.resetFields();
      setAddressDetails(null);
      setProfilePicture(null);
      setSelectedRoles([]);
    }
  }, [isEditMode, user, form]);

  // Handle address selection
  const handleAddressChange = (details?: Location) => {
    if (details) {
      form.setFieldsValue({ address: details.formatted_address });
      setAddressDetails(details);
    } else {
      form.setFieldsValue({ address: "" });
      setAddressDetails(null);
    }
  };

  // Submit main form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      if (values.date_of_birth) {
        values.date_of_birth = values.date_of_birth.format("YYYY-MM-DD");
      }

      const payload: TenantUser = {
        ...values,
        address: undefined,
        profile_picture: profilePicture ?? "",
        location: addressDetails
          ? {
              latitude: addressDetails.latitude ?? "",
              longitude: addressDetails.longitude ?? "",
              place_id: addressDetails.place_id ?? "",
              name: addressDetails.name ?? "",
              display_name: addressDetails.display_name ?? "",
              country: addressDetails.country ?? "",
              state: addressDetails.state ?? "",
              district: addressDetails.district ?? "",
              city: addressDetails.city ?? "",
              street: addressDetails.street ?? "",
              postal_code: addressDetails.postal_code ?? "",
              phone: addressDetails.phone ?? "",
            }
          : null,
      };

      if (isEditMode) {
        if (!checkFormChanges(payload, user?.user ?? {}, notify)) {
          setIsSubmitting(false);
          return;
        }
        await TenantUserService.updateUser(id!, payload);
      } else {
        await TenantUserService.createUser(payload);
      }
      navigate(navigationRoutes.USERS);
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save roles
  const handleSaveRoles = async () => {
    if (!hasChanges()) {
      return;
    }

    setIsSaving(true);
    try {
      await TenantUserRoleService.postRoleListByUserId(id!, {
        roles: selectedRoles,
      });
      await Promise.all([refetchRoleList(), refetchUser()]);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    if (!roleList || !selectedRoles) {
      return false;
    }
    if (roleList.length !== selectedRoles.length) {
      return true;
    }
    return !roleList.every(role => selectedRoles.includes(role));
  };
  // Loading States
  if (isEditMode && userLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && userError) {
    return <NotFound />;
  }

  if (roleLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const userDetailsTab = (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item name="profile_picture" label="Profile Picture">
        <CustomImageUpload
          shape="square"
          borderStyle="dashed"
          accept="image/*"
          className="w-full"
          initialUrl={profilePicture ?? undefined}
          onUploaded={response => {
            setProfilePicture(response[0]?.file_path);
          }}
          boxWidth={200}
          boxHeight={200}
        />
      </Form.Item>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Invalid email" },
          ]}
        >
          <Input placeholder="Enter email" disabled={isEditMode} />
        </Form.Item>

        <Form.Item
          name="first_name"
          label="First Name"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="First name" />
        </Form.Item>

        <Form.Item name="last_name" label="Last Name">
          <Input placeholder="Last name" />
        </Form.Item>

        <Form.Item name="full_name" label="Full Name">
          <Input placeholder="Full name" />
        </Form.Item>

        <Form.Item name="phone_number" label="Phone Number">
          <Input placeholder="Phone number" />
        </Form.Item>

        <Form.Item name="mobile_number" label="Mobile Number">
          <Input addonBefore="+91" placeholder="Mobile number" />
        </Form.Item>

        <Form.Item name="address" label="Address">
          <GoogleAddressInput
            value={form?.getFieldValue("address") ?? ""}
            placeholder="Search address..."
            onChange={handleAddressChange}
            countryRestriction="in"
            types={["geocode", "establishment"]}
          />
        </Form.Item>

        <Form.Item name="gender" label="Gender">
          <Select placeholder="Select gender">
            <Select.Option value="male">Male</Select.Option>
            <Select.Option value="female">Female</Select.Option>
            <Select.Option value="other">Other</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="date_of_birth" label="Date of Birth">
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="username" label="Username">
          <Input placeholder="Username" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { min: 6, message: "Password should be at least 6 characters" },
          ]}
        >
          <Input.Password
            placeholder={
              isEditMode ? "Leave blank to keep current" : "Enter password"
            }
          />
        </Form.Item>
      </div>
    </Form>
  );

  const rolesPermissionsTab = roleListLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <div className="mb-6 flex justify-end">
        <CustomButton
          type="primary"
          onClick={handleSaveRoles}
          loading={isSaving}
          disabled={!hasChanges() || isSaving}
        >
          {isSaving ? "Saving..." : "Save Roles"}
        </CustomButton>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {roleOptions?.length ? (
          roleOptions.map((role: any) => (
            <Checkbox
              key={role.value}
              checked={selectedRoles.includes(role.value)}
              onChange={e =>
                setSelectedRoles(prev =>
                  e.target.checked
                    ? [...prev, role.value]
                    : prev.filter(r => r !== role.value)
                )
              }
              disabled={isSaving}
            >
              {role.label}
            </Checkbox>
          ))
        ) : (
          <Empty description="No roles found" />
        )}
      </div>
    </>
  );

  const tabItems = [
    { key: "1", label: "User Details", children: userDetailsTab },
    {
      key: "2",
      label: "Roles & Permissions",
      children: rolesPermissionsTab,
    },
  ];

  return (
    <Card
      title={
        <PageHeader
          title={isEditMode ? "Edit User" : "Add New User"}
          onBack={() => navigate(navigationRoutes.USERS)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              htmlType="submit"
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Create User"}
            </CustomButton>
          }
        />
      }
    >
      <Tabs defaultActiveKey="1" items={tabItems} />
    </Card>
  );
};

export default UserFormPage;
